// Repositorio propio de `enterprise` (plan.md §3.1). Envuelve `@shared/api/http` directo — NO
// `enterprises.api.js` legado (ese sigue vivo como puente para otros consumidores, decisión L,
// tasks.md). zod ligero (constitution.md regla #5): el único mapeo real que lo justifica es la
// asimetría de `branches` (decisión A).
//
// Decisión as-is #1 (plan.md §3.1, decisión A de tasks.md): el as-is LEE `branches` como array
// (`Array.isArray(ent.branches) ? ent.branches : []`) y ESCRIBE `JSON.stringify(array)`. Esa
// asimetría se confina aquí: domain/application manejan SIEMPRE `Branch[]`, el `JSON.stringify`
// vive solo en `updateBranches`. NO se añade un `JSON.parse` de resiliencia en lectura — sería
// mejora, no paridad (ver improvements.md).

import { z } from 'zod';
import apiClient from '@shared/api/http';
import type { Branch, Enterprise, Id } from '../domain/enterprise.types';

const IdSchema = z.union([z.string(), z.number()]).transform(String);

// El dominio conserva `snake_case` (el mapper es identidad) y v1 responde camelCase: la
// conversión de claves vive aquí, en ambas direcciones, para no tocar domain/ ni los formularios.
const toSnakeKey = (key: string) => key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
const toCamelKey = (key: string) => key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());

function mapKeys(value: unknown, transform: (key: string) => string): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, inner]) => [transform(key), inner])
  );
}

// Passthrough: preserva campos extra del backend para el round-trip `{ ...b }` (spec.md §2,
// nota de Branch).
const BranchSchema = z
  .object({
    name: z.string().nullish(),
    city: z.string().nullish(),
    address: z.string().nullish(),
    phone: z.string().nullish(),
  })
  .passthrough();

// Lectura as-is: `Array.isArray(x) ? x : []`. El preprocess reproduce el guard; `.catch([])`
// cubre cualquier elemento inesperado sin tirar toda la carga (equivalente al "else []").
const BranchesSchema = z
  .preprocess((v) => (Array.isArray(v) ? v : []), z.array(BranchSchema))
  .catch([]);

const EnterpriseDtoSchema = z
  .object({
    id: IdSchema,
    name: z.string(),
    nit: z.string().nullish(),
    address: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    country_id: z.union([z.string(), z.number()]).nullish(),
    org_type: z.string().nullish(),
    logo_url: z.string().nullish(),
    branches: BranchesSchema, // ⇒ Branch[] SIEMPRE (nunca string en dominio)
  })
  .passthrough();

function toDomain(dto: z.infer<typeof EnterpriseDtoSchema>): Enterprise {
  return dto;
}

export const enterpriseRepository = {
  // GET /enterprises → Enterprise[] (el hook toma data[0] vía selectOwnerEnterprise, application/).
  async getAll(): Promise<Enterprise[]> {
    const { data } = await apiClient.get('/enterprises');
    return z
      .array(z.unknown())
      .parse(data)
      .map((item) => toDomain(EnterpriseDtoSchema.parse(mapKeys(item, toSnakeKey))));
  },

  // v1 actualiza la empresa con PATCH (mapping.md §S-3). Se envía el Record del form/logo tal
  // cual (as-is: sin normalizar, '' no null; decisión F).
  async update(id: Id, payload: Record<string, unknown>): Promise<Enterprise> {
    const { data } = await apiClient.patch(`/enterprises/${id}`, mapKeys(payload, toCamelKey));
    return toDomain(EnterpriseDtoSchema.parse(mapKeys(data, toSnakeKey)));
  },

  // UC-3: aquí y SOLO aquí vive el JSON.stringify (border-normalization, decisión A).
  async updateBranches(id: Id, branches: Branch[]): Promise<Enterprise> {
    const { data } = await apiClient.patch(`/enterprises/${id}`, {
      branches: JSON.stringify(branches),
    });
    return toDomain(EnterpriseDtoSchema.parse(mapKeys(data, toSnakeKey)));
  },

  // Lectura por id, alta y borrado de la entidad NO se portan: sin consumidor en este
  // contexto (decisión L, tasks.md).
};
